import React from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router';
import { Header } from '@app/navigation/header';
import { Body, Container, Body5, H5, Page } from '@ui';
import { BackArrowButton } from '@ui/buttons';
import { ROUTES } from '@constants/routes';
import { AI_TOOLS } from '@constants';
import { useCurrentUserToolsAvailable } from '@hooks/user/useCurrentUser';
import { DuplicateSpecForm } from '@features/scoreSpecDuplicate/DuplicateSpecForm';

const PageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

export const EDIT_SCORE_SPEC_HEADING_ID = 'edit-score-spec-heading';

// The simple score-spec mode. One screen, one form, no sub-routes — the whole reason it is a
// separate page from /advanced-score-spec rather than another tab inside it.
//
// "Edit" is the operator's word for it; duplication is the mechanism. An approved spec is frozen and
// there is no unlock endpoint, so editing one is authoring its successor — which is why the form
// still talks about copying a source and still produces a draft, and why the id gains a version.
//
// Same grant as the advanced editor (`score_spec_admin`), and for the same reason it is enforced
// here as well as on the server: the route is reachable by direct URL. It is a weaker door than it
// looks — client-side redirect only — but the API is the real one, and what this form creates is a
// draft, which runs nothing and can be deleted outright.
export const EditScoreSpec = () => {
  const navigate = useNavigate();
  // Present on /edit-score-spec/:specId, where the fetch screen sends a record it has already read.
  // Absent on the bare path, which still opens on an unanswered picker.
  const { specId } = useParams();
  const tools = useCurrentUserToolsAvailable();

  React.useEffect(() => {
    if (tools && tools.length > 0 && !tools.includes(AI_TOOLS.SCORE_SPEC_ADMIN)) {
      navigate(ROUTES.home);
    }
  }, [tools, navigate]);

  React.useEffect(() => {
    document.getElementById(EDIT_SCORE_SPEC_HEADING_ID)?.focus();
  }, []);

  return (
    <PageWrapper>
      <Header />
      <Body withFooter>
        {/* A fixed measure, not a share of the viewport. This ran at 95% while the prompt and the
            output contract were read side by side; now that the steps stack, the width would only be
            stretching single-line inputs and prose across a monitor. Wider than the paste and fetch
            modes because the shape editor puts a name, a type and a path on one row. */}
        <Page width="1200px">
          {/* Back to the record this came from, when it came from one. Arriving here by way of a
              spec and leaving to the home page would drop the thread. */}
          <BackArrowButton
            onClick={() => navigate(specId ? `${ROUTES.getScoreSpec}/${specId}` : ROUTES.home)}
            aria-label={specId ? 'Back to the spec' : 'Back to home'}
          />
          <Container top={48}>
            <H5 id={EDIT_SCORE_SPEC_HEADING_ID} tabIndex={-1}>
              Edit a Score Spec
            </H5>
            <Container top={8}>
              <Body5 isLowContrast>
                An approved spec is frozen, so editing one means authoring its successor: pick it, give it a new name
                and a new prompt, and get a draft. Everything else — the trigger, the documents, the model and the
                output contract — is carried across untouched.
              </Body5>
            </Container>
            <Container top={24}>
              <DuplicateSpecForm initialSourceId={specId} />
            </Container>
          </Container>
        </Page>
      </Body>
    </PageWrapper>
  );
};

export default EditScoreSpec;
